/**
 * "Add to Home Screen" for one folder (#218).
 *
 * Safari reads a tab's web app manifest ONCE, when the page loads, and keeps it
 * until the next full load (WebKit's DocumentLoader caches it; seen in the
 * device's Web Inspector on stagetest2, 2026-10-21). The manifest has no
 * `start_url`, so the saved icon opens the URL the tab LOADED — after in-app
 * navigation, that is the wrong page. No script can make Safari re-read it.
 *
 * So the action does the one thing that works: a full page load AT the folder,
 * then a hint to finish in Safari's Share sheet. The hint is carried across the
 * load in sessionStorage, never in the URL — the URL is what gets saved.
 */

const HINT_KEY = 'papan:a2hs-hint'

/** Where offering it makes sense: iOS Safari, in a tab — not inside an
 *  installed home-screen app (`navigator.standalone`), and not other browsers
 *  (they have no `standalone` flag, or their own install flow). */
export function canOfferAddToHomeScreen(nav: Navigator = navigator, win: Window = window): boolean {
  const standalone = (nav as Navigator & { standalone?: boolean }).standalone
  if (standalone !== false) return false
  try {
    if (win.matchMedia?.('(display-mode: standalone)').matches) return false
  } catch { /* no matchMedia — the standalone flag above already answered */ }
  return true
}

/** Reload the page AT `path` (a fresh manifest), and leave a hint for it. */
export function startAddToHomeScreen(path: string, win: Window = window): void {
  try { win.sessionStorage.setItem(HINT_KEY, path) } catch { /* private mode: no hint, the load still helps */ }
  const here = win.location.pathname + win.location.search
  if (here === path) win.location.reload()
  else win.location.assign(path)
}

/** True ONCE, on the page the action loaded; the hint is removed on read. */
export function takeAddToHomeScreenHint(win: Window = window): boolean {
  let path: string | null = null
  try {
    path = win.sessionStorage.getItem(HINT_KEY)
    win.sessionStorage.removeItem(HINT_KEY)
  } catch { return false }
  return !!path && path === win.location.pathname + win.location.search
}
